import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { AffiliateBadge } from './AffiliateBadge';

/** Props of {@link IntroScreen}. */
export interface IntroScreenProps {
  /** Display name of the affiliate who invited the trader, or `null` (no badge, no placeholder). */
  readonly affiliateName: string | null;
  /** Called when the trader presses "Get started". */
  readonly onStart: () => void;
}

/**
 * Screen E1 · Intro (specs/S3-intro/spec.md; US-01): three sentences before anything is asked,
 * the reading-only promise (I6), the inviting affiliate when there is one, and one button.
 *
 * Presentational: it fetches nothing and keeps no state (R12). It owns its `main` landmark because
 * it renders outside `AppLayout`, with no tab bar (R6). Texts are typed keys, never literals (I12).
 */
export function IntroScreen({ affiliateName, onStart }: IntroScreenProps): ReactElement {
  const { t } = useTranslation();
  const title = t('app.title');
  const steps = [
    t('onboarding.intro.step1'),
    t('onboarding.intro.step2'),
    t('onboarding.intro.step3'),
  ];
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        width: '100%',
        maxWidth: 560,
        mx: 'auto',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        px: 2.5,
        pt: 3,
        pb: 3.5,
      }}
    >
      {/* The brand name is not translated; "Mate" takes the brand colour, as on Home (P8). */}
      <Box aria-hidden sx={{ fontWeight: 700, fontSize: 20, letterSpacing: '-0.01em' }}>
        {title.slice(0, 2)}
        <Box component="span" sx={{ color: 'primary.main' }}>
          {title.slice(2)}
        </Box>
      </Box>
      {affiliateName === null ? null : <AffiliateBadge name={affiliateName} />}
      <Typography
        component="h1"
        sx={{ fontSize: 30, lineHeight: 1.15, letterSpacing: '-0.02em', fontWeight: 700 }}
      >
        {t('onboarding.intro.title')}
      </Typography>
      <Box
        component="ol"
        sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {steps.map((text, index) => (
          <Box component="li" key={text} sx={{ display: 'flex', gap: 1.75 }}>
            {/* The list already numbers the items for assistive technology (R10). */}
            <Box
              component="span"
              aria-hidden
              sx={(theme) => ({
                flex: 'none',
                width: 32,
                height: 32,
                borderRadius: '50%',
                border: `1.5px solid ${theme.palette.primary.main}`,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
              })}
            >
              {index + 1}
            </Box>
            <Box component="span" sx={{ lineHeight: 1.45 }}>
              {text}
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
          {t('onboarding.intro.readOnlyNote')}
        </Typography>
        {/* P3: an in-page button as on mockup 2, not Telegram's MainButton (C3 R15 bridge, `lib/telegram/MainButton`). */}
        <Button variant="contained" size="large" fullWidth onClick={onStart} sx={{ minHeight: 52 }}>
          {t('onboarding.intro.start')}
        </Button>
      </Box>
    </Box>
  );
}
